import axiosInstance from "../common/axiosInstance";
import {
  AccessZone,
  AccessZoneFormData,
  AccessAssignment,
  AccessAssignmentFormData,
  Camera,
  CameraFormData,
  CameraHealthStatus,
  MotionAlert,
  SurveillanceOverview,
  SurveillanceHeatmapResponse,
  VideoClipLinkPayload,
  Visitor,
  VisitorFormData,
  SecurityIncident,
  SecurityIncidentFormData,
  Vehicle,
  VehicleFormData,
  SecurityApiResponse,
  PaginatedResponse,
  SecuritySearchFilters,
  PaginationParams,
  SecurityDashboardStats,
  CommandCenterCctvGridData,
  CommandCenterGuardDeploymentData,
  CommandCenterThreatHeatmapData,
  CommandCenterDashboardSummaryData,
  EmergencyBroadcastPayload,
  EmergencyBroadcastResult,
} from "../types/security_types";

// ============================================================================
// ACCESS ZONE API
// ============================================================================

export const getAllAccessZones = async (
  filters?: SecuritySearchFilters
): Promise<AccessZone[]> => {
  const params: any = {};
  if (filters?.region) params.region = filters.region;
  if (filters?.premise) params.premise = filters.premise;
  if (filters?.searchQuery) params.search = filters.searchQuery;

  const response = await axiosInstance.get<SecurityApiResponse<AccessZone[]>>(
    "/api/security/access-zones",
    { params }
  );
  return response.data.data || response.data.message || [];
};

export const getAccessZoneById = async (id: string): Promise<AccessZone> => {
  const response = await axiosInstance.get<SecurityApiResponse<AccessZone>>(
    `/api/security/access-zones/${id}`
  );
  return (response.data.data || response.data.message) as AccessZone;
};

export const createAccessZone = async (
  data: AccessZoneFormData
): Promise<AccessZone> => {
  const response = await axiosInstance.post<SecurityApiResponse<AccessZone>>(
    "/api/security/access-zones",
    data
  );
  return (response.data.data || response.data.message) as AccessZone;
};

export const updateAccessZone = async (
  id: string,
  data: Partial<AccessZoneFormData>
): Promise<AccessZone> => {
  const response = await axiosInstance.put<SecurityApiResponse<AccessZone>>(
    `/api/security/access-zones/${id}`,
    data
  );
  return (response.data.data || response.data.message) as AccessZone;
};

export const deleteAccessZone = async (id: string): Promise<void> => {
  await axiosInstance.delete<SecurityApiResponse<void>>(
    `/api/security/access-zones/${id}`
  );
};

// ============================================================================
// ACCESS ASSIGNMENT API
// ============================================================================

export const getAllAccessAssignments = async (
  filters?: SecuritySearchFilters,
  pagination?: PaginationParams
): Promise<PaginatedResponse<AccessAssignment>> => {
  const params: any = {};
  if (filters) {
    if (filters.region) params.region = filters.region;
    if (filters.premise) params.premise = filters.premise;
    if (filters.zoneId) params.zoneId = filters.zoneId;
    if (filters.status && filters.status !== "ALL") {
      params.status = filters.status;
    }
    if (filters.searchQuery) params.where = JSON.stringify({ $or: [
      { userId: { $regex: filters.searchQuery, $options: 'i' } },
      { zoneName: { $regex: filters.searchQuery, $options: 'i' } }
    ]});
  }
  if (pagination) {
    params.limit = pagination.limit || 20;
    params.skip = pagination.page ? (pagination.page - 1) * (pagination.limit || 20) : 0;
  }
  
  const response = await axiosInstance.get<SecurityApiResponse<any>>(
    "/api/security/access-assignments",
    { params }
  );
  
  const backendData = response.data.data || response.data.message;
  if (backendData && Array.isArray(backendData)) {
    // Backend returned array directly
    return {
      data: backendData,
      total: backendData.length,
      page: pagination?.page || 1,
      limit: pagination?.limit || 20,
      totalPages: Math.ceil(backendData.length / (pagination?.limit || 20))
    };
  }
  
  // Backend returned paginated format
  if (backendData && backendData.data) {
    return {
      data: backendData.data,
      total: backendData.total || backendData.data.length,
      page: pagination?.page || Math.floor((backendData.skip || 0) / (backendData.limit || 20)) + 1,
      limit: backendData.limit || 20,
      totalPages: Math.ceil((backendData.total || backendData.data.length) / (backendData.limit || 20))
    };
  }
  
  return {
    data: [],
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 0
  };
};

export const getAccessAssignmentById = async (
  id: string
): Promise<AccessAssignment> => {
  const response = await axiosInstance.get<SecurityApiResponse<AccessAssignment>>(
    `/api/security/access-assignments/${id}`
  );
  return (response.data.data || response.data.message) as AccessAssignment;
};

function assignmentApiBody(
  data: AccessAssignmentFormData & { zoneName?: string }
): Record<string, unknown> {
  return {
    userType: data.userType,
    userId: data.userId,
    visitorId: data.visitorId,
    vendorId: data.vendorId,
    zone: data.zoneId,
    zoneId: data.zoneId,
    zoneName: data.zoneName,
    accessType: data.accessType,
    startDateTime: data.startDateTime,
    endDateTime: data.endDateTime,
    approvalAuthority: data.approvalAuthority,
  };
}

export const createAccessAssignment = async (
  data: AccessAssignmentFormData & { zoneName?: string }
): Promise<AccessAssignment> => {
  const response = await axiosInstance.post<SecurityApiResponse<AccessAssignment>>(
    "/api/security/access-assignments",
    assignmentApiBody(data)
  );
  return (response.data.data || response.data.message) as AccessAssignment;
};

export const updateAccessAssignment = async (
  id: string,
  data: Partial<AccessAssignmentFormData> & { zoneName?: string }
): Promise<AccessAssignment> => {
  const body: Record<string, unknown> = { ...data };
  if (data.zoneId) {
    body.zone = data.zoneId;
    if (data.zoneName) body.zoneName = data.zoneName;
  }
  delete body.zoneId;
  const response = await axiosInstance.put<SecurityApiResponse<AccessAssignment>>(
    `/api/security/access-assignments/${id}`,
    body
  );
  return (response.data.data || response.data.message) as AccessAssignment;
};

export const revokeAccessAssignment = async (id: string): Promise<void> => {
  await axiosInstance.put(`/api/security/access-assignments/${id}/revoke`);
};

export const approveAccessAssignment = async (
  id: string
): Promise<AccessAssignment> => {
  const response = await axiosInstance.post<SecurityApiResponse<AccessAssignment>>(
    `/api/security/access-assignments/${id}/approve`
  );
  return (response.data.data || response.data.message) as AccessAssignment;
};

export const getAccessAssignmentQRCode = async (
  id: string
): Promise<{ qrCode?: string; qrData?: unknown } | null> => {
  const response = await axiosInstance.get(
    `/api/security/access-assignments/${id}/qr-code`
  );
  const body = response.data as {
    qrCode?: string;
    qrData?: unknown;
  };
  return body || null;
};

export interface AccessControlOverview {
  zones: { total: number; active: number };
  assignments: {
    activeNow: number;
    pendingApproval: number;
    revokedLast7d: number;
  };
  accessLogsLast7d: { granted: number; denied: number };
}

export const getAccessControlOverview =
  async (): Promise<AccessControlOverview | null> => {
    const response = await axiosInstance.get(
      "/api/security/access-control/overview"
    );
    const body = response.data as { data?: AccessControlOverview };
    return body.data ?? null;
  };

export interface AccessLogInput {
  accessType: "Granted" | "Denied";
  zone?: string;
  location?: string;
  userId?: string;
  userName?: string;
  deviceId?: string;
  reason?: string;
  accessDateTime?: string;
}

export const createAccessLog = async (
  payload: AccessLogInput
): Promise<Record<string, unknown> | null> => {
  const response = await axiosInstance.post("/api/security/access-logs", payload);
  const body = response.data as { data?: Record<string, unknown> };
  return body.data ?? null;
};

// ============================================================================
// CAMERA API
// ============================================================================

export const getAllCameras = async (
  filters?: SecuritySearchFilters & { limit?: number }
): Promise<Camera[]> => {
  const params: Record<string, string | number> = {
    limit: filters?.limit ?? 500,
  };
  if (filters?.zoneId) params.zoneId = filters.zoneId;
  if (filters?.status) params.status = filters.status;
  if (filters?.searchQuery) params.search = filters.searchQuery;

  const response = await axiosInstance.get<{
    success?: boolean;
    data?: Camera[];
    message?: unknown;
  }>("/api/security/cameras", { params });

  const raw = response.data?.data ?? response.data?.message;
  if (Array.isArray(raw)) return raw as Camera[];
  return [];
};

export const seedSampleCameras = async (): Promise<{ inserted: number }> => {
  const response = await axiosInstance.post<{
    data?: { inserted: number };
  }>("/api/security/cameras/seed-samples");
  return response.data?.data ?? { inserted: 0 };
};

export const updateCameraHealth = async (
  id: string,
  payload: { healthStatus: CameraHealthStatus; lastHealthCheck?: string }
): Promise<Camera> => {
  const response = await axiosInstance.put<SecurityApiResponse<Camera>>(
    `/api/security/cameras/${id}/health-status`,
    payload
  );
  return (response.data.data || response.data.message) as Camera;
};

export const getSurveillanceOverview = async (): Promise<SurveillanceOverview> => {
  const response = await axiosInstance.get<{
    data?: SurveillanceOverview;
  }>("/api/security/surveillance/overview");
  const d = response.data?.data;
  if (d) return d;
  return {
    cameras: { total: 0, active: 0, maintenance: 0, onlineHealth: 0 },
    motion: { unacknowledged: 0, last24h: 0 },
    recentMotionAlerts: [],
  };
};

export const getMotionAlerts = async (opts?: {
  limit?: number;
  onlyOpen?: boolean;
}): Promise<MotionAlert[]> => {
  const params: Record<string, string | number> = {};
  if (opts?.limit) params.limit = opts.limit;
  if (opts?.onlyOpen) params.onlyOpen = "true";

  const response = await axiosInstance.get<{ data?: MotionAlert[] }>(
    "/api/security/surveillance/motion-alerts",
    { params }
  );
  return response.data?.data ?? [];
};

export const createMotionAlert = async (body: {
  cameraId: string;
  cameraName?: string;
  severity?: string;
  message?: string;
  detectedAt?: string;
}): Promise<MotionAlert> => {
  const response = await axiosInstance.post<{ data?: MotionAlert }>(
    "/api/security/surveillance/motion-alerts",
    body
  );
  return response.data?.data as MotionAlert;
};

export const acknowledgeMotionAlert = async (id: string): Promise<MotionAlert> => {
  const response = await axiosInstance.put<{ data?: MotionAlert }>(
    `/api/security/surveillance/motion-alerts/${id}/ack`
  );
  return response.data?.data as MotionAlert;
};

export const getSurveillanceHeatmap = async (): Promise<SurveillanceHeatmapResponse> => {
  const response = await axiosInstance.get<{ data?: SurveillanceHeatmapResponse }>(
    "/api/security/surveillance/heatmap"
  );
  const d = response.data?.data;
  if (d?.zones) return d;
  return { zones: [], methodology: "" };
};

export const linkVideoClipsToIncident = async (
  incidentId: string,
  videoClips: VideoClipLinkPayload | VideoClipLinkPayload[]
): Promise<void> => {
  const payload = Array.isArray(videoClips) ? videoClips : [videoClips];
  await axiosInstance.post(`/api/security/incidents/${incidentId}/link-video-clips`, {
    videoClips: payload,
  });
};

export const getCameraById = async (id: string): Promise<Camera> => {
  const response = await axiosInstance.get<SecurityApiResponse<Camera>>(
    `/api/security/cameras/${id}`
  );
  return (response.data.data || response.data.message) as Camera;
};

export const createCamera = async (data: CameraFormData): Promise<Camera> => {
  const response = await axiosInstance.post<SecurityApiResponse<Camera>>(
    "/api/security/cameras",
    data
  );
  return (response.data.data || response.data.message) as Camera;
};

export const updateCamera = async (
  id: string,
  data: Partial<CameraFormData>
): Promise<Camera> => {
  const response = await axiosInstance.put<SecurityApiResponse<Camera>>(
    `/api/security/cameras/${id}`,
    data
  );
  return (response.data.data || response.data.message) as Camera;
};

export const deleteCamera = async (id: string): Promise<void> => {
  await axiosInstance.delete<SecurityApiResponse<void>>(
    `/api/security/cameras/${id}`
  );
};

// ============================================================================
// VISITOR API
// ============================================================================

export const getAllVisitors = async (
  filters?: SecuritySearchFilters,
  pagination?: PaginationParams
): Promise<PaginatedResponse<Visitor>> => {
  const params: any = {};
  if (filters) {
    if (filters.region) params.region = filters.region;
    if (filters.premise) params.premise = filters.premise;
    if (filters.status) params.status = filters.status;
    if (filters.searchQuery) params.search = filters.searchQuery;
  }
  if (pagination) {
    params.limit = pagination.limit || 20;
    params.skip = pagination.page ? (pagination.page - 1) * (pagination.limit || 20) : 0;
  }
  
  const response = await axiosInstance.get<SecurityApiResponse<any>>(
    "/api/security/visitors",
    { params }
  );
  
  const backendData = response.data.data || response.data.message;
  if (backendData && Array.isArray(backendData)) {
    return {
      data: backendData,
      total: backendData.length,
      page: pagination?.page || 1,
      limit: pagination?.limit || 20,
      totalPages: Math.ceil(backendData.length / (pagination?.limit || 20))
    };
  }
  
  if (backendData && backendData.data) {
    return {
      data: backendData.data,
      total: backendData.total || backendData.data.length,
      page: pagination?.page || Math.floor((backendData.skip || 0) / (backendData.limit || 20)) + 1,
      limit: backendData.limit || 20,
      totalPages: Math.ceil((backendData.total || backendData.data.length) / (backendData.limit || 20))
    };
  }
  
  return {
    data: [],
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 0
  };
};

export const getVisitorById = async (id: string): Promise<Visitor> => {
  const response = await axiosInstance.get<SecurityApiResponse<Visitor>>(
    `/api/security/visitors/${id}`
  );
  return (response.data.data || response.data.message) as Visitor;
};

export const createVisitor = async (data: VisitorFormData): Promise<Visitor> => {
  const response = await axiosInstance.post<SecurityApiResponse<Visitor>>(
    "/api/security/visitors",
    data
  );
  return (response.data.data || response.data.message) as Visitor;
};

export const updateVisitor = async (
  id: string,
  data: Partial<VisitorFormData>
): Promise<Visitor> => {
  const response = await axiosInstance.put<SecurityApiResponse<Visitor>>(
    `/api/security/visitors/${id}`,
    data
  );
  return (response.data.data || response.data.message) as Visitor;
};

export const checkInVisitor = async (id: string): Promise<Visitor> => {
  const response = await axiosInstance.post<SecurityApiResponse<Visitor>>(
    `/api/security/visitors/${id}/check-in`
  );
  return (response.data.data || response.data.message) as Visitor;
};

export const checkOutVisitor = async (id: string): Promise<Visitor> => {
  const response = await axiosInstance.post<SecurityApiResponse<Visitor>>(
    `/api/security/visitors/${id}/check-out`
  );
  return (response.data.data || response.data.message) as Visitor;
};

export const blacklistVisitor = async (
  id: string,
  reason?: string
): Promise<Visitor> => {
  const response = await axiosInstance.post<SecurityApiResponse<Visitor>>(
    `/api/security/visitors/${id}/blacklist`,
    { reason }
  );
  return (response.data.data || response.data.message) as Visitor;
};

export const unblacklistVisitor = async (id: string): Promise<Visitor> => {
  const response = await axiosInstance.post<SecurityApiResponse<Visitor>>(
    `/api/security/visitors/${id}/unblacklist`
  );
  return (response.data.data || response.data.message) as Visitor;
};

export const verifyVisitorIdentity = async (
  id: string,
  notes?: string
): Promise<Visitor> => {
  const response = await axiosInstance.post<SecurityApiResponse<Visitor>>(
    `/api/security/visitors/${id}/verify-id`,
    { notes }
  );
  return (response.data.data || response.data.message) as Visitor;
};

export const seedSampleVisitors = async (): Promise<{ inserted: number }> => {
  const response = await axiosInstance.post<{
    data?: { inserted: number };
    message?: string;
  }>("/api/security/visitors/seed-samples");
  return response.data?.data ?? { inserted: 0 };
};

export const generateVisitorQRCode = async (
  id: string,
  download: boolean = false,
  format: "png" | "svg" = "png"
): Promise<any> => {
  if (download) {
    const response = await axiosInstance.get(
      `/api/security/visitors/${id}/qr-code/download?format=${format}`,
      {
        responseType: "blob",
      }
    );
    return response.data;
  } else {
    const response = await axiosInstance.get<SecurityApiResponse<any>>(
      `/api/security/visitors/${id}/qr-code`
    );
    return response.data.data || response.data;
  }
};

// ============================================================================
// SECURITY INCIDENT API
// ============================================================================

export const getAllSecurityIncidents = async (
  filters?: SecuritySearchFilters,
  pagination?: PaginationParams
): Promise<PaginatedResponse<SecurityIncident>> => {
  const params: any = {};
  if (filters) {
    if (filters.region) params.region = filters.region;
    if (filters.premise) params.premise = filters.premise;
    if (filters.zoneId) params.zoneId = filters.zoneId;
    if (filters.severity) params.severity = filters.severity;
    if (filters.status) params.status = filters.status;
    if (filters.dateFrom) params.startDate = filters.dateFrom;
    if (filters.dateTo) params.endDate = filters.dateTo;
    if (filters.searchQuery) params.search = filters.searchQuery;
  }
  if (pagination) {
    params.limit = pagination.limit || 20;
    params.skip = pagination.page ? (pagination.page - 1) * (pagination.limit || 20) : 0;
  }
  
  const response = await axiosInstance.get<SecurityApiResponse<any>>(
    "/api/security/incidents",
    { params }
  );
  
  const backendData = response.data.data || response.data.message;
  if (backendData && Array.isArray(backendData)) {
    return {
      data: backendData,
      total: backendData.length,
      page: pagination?.page || 1,
      limit: pagination?.limit || 20,
      totalPages: Math.ceil(backendData.length / (pagination?.limit || 20))
    };
  }
  
  if (backendData && backendData.data) {
    return {
      data: backendData.data,
      total: backendData.total || backendData.data.length,
      page: pagination?.page || Math.floor((backendData.skip || 0) / (backendData.limit || 20)) + 1,
      limit: backendData.limit || 20,
      totalPages: Math.ceil((backendData.total || backendData.data.length) / (backendData.limit || 20))
    };
  }
  
  return {
    data: [],
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 0
  };
};

export const getSecurityIncidentById = async (
  id: string
): Promise<SecurityIncident> => {
  const response = await axiosInstance.get<SecurityApiResponse<SecurityIncident>>(
    `/api/security/incidents/${id}`
  );
  return (response.data.data || response.data.message) as SecurityIncident;
};

export const createSecurityIncident = async (
  data: SecurityIncidentFormData
): Promise<SecurityIncident> => {
  const requestData: Record<string, unknown> = {
    incidentType: data.incidentType,
    location: data.location,
    incidentDateTime: data.dateTime
      ? new Date(data.dateTime).toISOString()
      : new Date().toISOString(),
    severity: data.severity,
    description: data.description,
    immediateActionTaken: data.immediateActionTaken || "",
    escalatedTo: data.escalatedTo || [],
    evidence: Array.isArray(data.evidenceUrls) ? data.evidenceUrls.filter(Boolean) : [],
    linkedLogRefs: Array.isArray(data.linkedLogRefs) ? data.linkedLogRefs : [],
  };

  if (data.zoneId) {
    requestData.zone = data.zoneId;
  }

  const response = await axiosInstance.post<SecurityApiResponse<SecurityIncident>>(
    "/api/security/incidents",
    requestData
  );
  return (response.data.data || response.data.message) as SecurityIncident;
};

export const updateSecurityIncident = async (
  id: string,
  data: Partial<SecurityIncidentFormData>
): Promise<SecurityIncident> => {
  // Map frontend field names to backend field names
  const requestData: any = {};
  
  if (data.incidentType) requestData.incidentType = data.incidentType;
  if (data.location) requestData.location = data.location;
  if (data.dateTime) requestData.incidentDateTime = new Date(data.dateTime).toISOString();
  if (data.severity) requestData.severity = data.severity;
  if (data.description) requestData.description = data.description;
  if (data.immediateActionTaken !== undefined) requestData.immediateActionTaken = data.immediateActionTaken;
  if (data.escalatedTo) requestData.escalatedTo = data.escalatedTo;
  if (data.zoneId) requestData.zone = data.zoneId;
  if (data.evidenceUrls) requestData.evidence = data.evidenceUrls.filter(Boolean);
  if (data.linkedLogRefs) requestData.linkedLogRefs = data.linkedLogRefs;
  if (data.globalIncidentId) requestData.globalIncidentId = data.globalIncidentId;

  const response = await axiosInstance.put<SecurityApiResponse<SecurityIncident>>(
    `/api/security/incidents/${id}`,
    requestData
  );
  return (response.data.data || response.data.message) as SecurityIncident;
};

export interface SecurityEscalationMatrixRow {
  severity: string;
  slaMinutes: number;
  notify: string[];
  channels: string[];
}

export const getSecurityEscalationMatrix = async (): Promise<{
  matrix: SecurityEscalationMatrixRow[];
  note?: string;
}> => {
  const response = await axiosInstance.get<{
    data?: { matrix: SecurityEscalationMatrixRow[]; note?: string };
  }>("/api/security/incidents/escalation-matrix");
  return (
    response.data?.data ?? {
      matrix: [],
    }
  );
};

export const triggerSecurityPanicIncident = async (body: {
  panicSource: "mobile" | "wall_mounted";
  location?: string;
  zone?: string;
  description?: string;
  coordinates?: Record<string, unknown>;
}): Promise<SecurityIncident> => {
  const response = await axiosInstance.post<SecurityApiResponse<SecurityIncident>>(
    "/api/security/incidents/panic",
    body
  );
  return (response.data.data || response.data.message) as SecurityIncident;
};

export const assignSecurityIncidentInvestigator = async (
  id: string,
  payload: { investigatorId: string; investigatorName: string }
): Promise<SecurityIncident> => {
  const response = await axiosInstance.post<SecurityApiResponse<SecurityIncident>>(
    `/api/security/incidents/${id}/assign-investigator`,
    payload
  );
  return (response.data.data || response.data.message) as SecurityIncident;
};

export const addSecurityIncidentInvestigationNote = async (
  id: string,
  payload: { note: string; noteType?: string }
): Promise<SecurityIncident> => {
  const response = await axiosInstance.post<SecurityApiResponse<SecurityIncident>>(
    `/api/security/incidents/${id}/investigation-note`,
    payload
  );
  return (response.data.data || response.data.message) as SecurityIncident;
};

export const completeSecurityIncidentInvestigation = async (
  id: string,
  payload: { investigationReport?: string }
): Promise<SecurityIncident> => {
  const response = await axiosInstance.post<SecurityApiResponse<SecurityIncident>>(
    `/api/security/incidents/${id}/complete-investigation`,
    payload
  );
  return (response.data.data || response.data.message) as SecurityIncident;
};

export const refreshSecurityIncidentSLA = async (
  id: string
): Promise<SecurityIncident> => {
  const response = await axiosInstance.post<SecurityApiResponse<SecurityIncident>>(
    `/api/security/incidents/${id}/update-sla`
  );
  return (response.data.data || response.data.message) as SecurityIncident;
};

// ============================================================================
// VEHICLE API
// ============================================================================

export const getAllVehicles = async (
  filters?: SecuritySearchFilters,
  pagination?: PaginationParams
): Promise<PaginatedResponse<Vehicle>> => {
  const params: any = {};
  if (filters) {
    if (filters.region) params.region = filters.region;
    if (filters.premise) params.premise = filters.premise;
    if (filters.status) params.status = filters.status;
    if (filters.searchQuery) params.where = JSON.stringify({ $or: [
      { vehicleNumber: { $regex: filters.searchQuery, $options: 'i' } },
      { ownerName: { $regex: filters.searchQuery, $options: 'i' } }
    ]});
  }
  if (pagination) {
    params.limit = pagination.limit || 20;
    params.skip = pagination.page ? (pagination.page - 1) * (pagination.limit || 20) : 0;
  }
  
  const response = await axiosInstance.get<SecurityApiResponse<any>>(
    "/api/security/vehicles",
    { params }
  );
  
  const backendData = response.data.data || response.data.message;
  if (backendData && Array.isArray(backendData)) {
    return {
      data: backendData,
      total: backendData.length,
      page: pagination?.page || 1,
      limit: pagination?.limit || 20,
      totalPages: Math.ceil(backendData.length / (pagination?.limit || 20))
    };
  }
  
  if (backendData && backendData.data) {
    return {
      data: backendData.data,
      total: backendData.total || backendData.data.length,
      page: pagination?.page || Math.floor((backendData.skip || 0) / (backendData.limit || 20)) + 1,
      limit: backendData.limit || 20,
      totalPages: Math.ceil((backendData.total || backendData.data.length) / (backendData.limit || 20))
    };
  }
  
  return {
    data: [],
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 0
  };
};

export const getVehicleById = async (id: string): Promise<Vehicle> => {
  const response = await axiosInstance.get<SecurityApiResponse<Vehicle>>(
    `/api/security/vehicles/${id}`
  );
  return (response.data.data || response.data.message) as Vehicle;
};

export const createVehicle = async (data: VehicleFormData): Promise<Vehicle> => {
  const response = await axiosInstance.post<SecurityApiResponse<Vehicle>>(
    "/api/security/vehicles",
    data
  );
  return (response.data.data || response.data.message) as Vehicle;
};

export const updateVehicle = async (
  id: string,
  data: Partial<VehicleFormData>
): Promise<Vehicle> => {
  const response = await axiosInstance.put<SecurityApiResponse<Vehicle>>(
    `/api/security/vehicles/${id}`,
    data
  );
  return (response.data.data || response.data.message) as Vehicle;
};

export const deleteVehicle = async (id: string): Promise<void> => {
  await axiosInstance.delete<SecurityApiResponse<void>>(
    `/api/security/vehicles/${id}`
  );
};

export const logVehicleEntry = async (
  id: string,
  body?: { entryTime?: string; gateNumber?: string }
): Promise<Vehicle> => {
  const response = await axiosInstance.post<SecurityApiResponse<Vehicle>>(
    `/api/security/vehicles/${id}/entry`,
    body || {}
  );
  return (response.data.data || response.data.message) as Vehicle;
};

export const logVehicleExit = async (
  id: string,
  body?: { exitTime?: string; gateNumber?: string }
): Promise<Vehicle> => {
  const response = await axiosInstance.post<SecurityApiResponse<Vehicle>>(
    `/api/security/vehicles/${id}/exit`,
    body || {}
  );
  return (response.data.data || response.data.message) as Vehicle;
};

export const markVehicleRestricted = async (
  id: string,
  body?: { reason?: string; restrictedUntil?: string }
): Promise<Vehicle> => {
  const response = await axiosInstance.post<SecurityApiResponse<Vehicle>>(
    `/api/security/vehicles/${id}/mark-restricted`,
    body || {}
  );
  return (response.data.data || response.data.message) as Vehicle;
};

export const clearVehicleRestriction = async (id: string): Promise<Vehicle> => {
  const response = await axiosInstance.post<SecurityApiResponse<Vehicle>>(
    `/api/security/vehicles/${id}/clear-restriction`,
    {}
  );
  return (response.data.data || response.data.message) as Vehicle;
};

export const checkRestrictedVehicleAtGate = async (body: {
  vehicleNumber: string;
  location?: string;
}): Promise<{
  isRestricted: boolean;
  alert?: boolean;
  vehicle?: Vehicle;
  message?: string;
}> => {
  const response = await axiosInstance.post<SecurityApiResponse<any>>(
    "/api/security/vehicles/check-restricted",
    body
  );
  const d = response.data as any;
  return {
    isRestricted: !!d.isRestricted,
    alert: !!d.alert,
    vehicle: d.vehicle,
    message: d.message,
  };
};

export const anprReadPlate = async (body: {
  rawPlate: string;
  gateNumber?: string;
  location?: string;
  autoLogEntry?: boolean;
}): Promise<Record<string, unknown>> => {
  const response = await axiosInstance.post("/api/security/vehicles/anpr-read", body);
  return (response.data || {}) as Record<string, unknown>;
};

export interface ParkingSlot {
  _id?: string;
  id?: string;
  slotNumber?: string;
  zone?: string;
  parkingLevel?: string;
  status?: string;
  currentVehicleId?: string | null;
  createdAt?: string;
}

export const getParkingSlots = async (
  pagination?: PaginationParams
): Promise<PaginatedResponse<ParkingSlot>> => {
  const params: Record<string, number> = {};
  if (pagination) {
    params.limit = pagination.limit || 100;
    params.skip = pagination.page ? (pagination.page - 1) * (pagination.limit || 100) : 0;
  }
  const response = await axiosInstance.get<SecurityApiResponse<any>>(
    "/api/security/parking-slots",
    { params }
  );
  const backendData = response.data.data || response.data.message;
  if (backendData?.data && Array.isArray(backendData.data)) {
    return {
      data: backendData.data,
      total: backendData.total || backendData.data.length,
      page: pagination?.page || 1,
      limit: backendData.limit || 100,
      totalPages: Math.ceil(
        (backendData.total || backendData.data.length) / (backendData.limit || 100)
      ),
    };
  }
  if (Array.isArray(backendData)) {
    return {
      data: backendData,
      total: backendData.length,
      page: 1,
      limit: backendData.length,
      totalPages: 1,
    };
  }
  return { data: [], total: 0, page: 1, limit: 100, totalPages: 0 };
};

export const createParkingSlot = async (
  data: Partial<ParkingSlot> & { slotNumber: string }
): Promise<ParkingSlot> => {
  const response = await axiosInstance.post<SecurityApiResponse<ParkingSlot>>(
    "/api/security/parking-slots",
    data
  );
  return (response.data.data || response.data.message) as ParkingSlot;
};

export const assignParkingSlot = async (
  slotId: string,
  body: { vehicleId: string; reservationEndTime?: string }
): Promise<ParkingSlot> => {
  const response = await axiosInstance.post<SecurityApiResponse<ParkingSlot>>(
    `/api/security/parking-slots/${slotId}/assign`,
    body
  );
  return (response.data.data || response.data.message) as ParkingSlot;
};

export const releaseParkingSlot = async (slotId: string): Promise<ParkingSlot> => {
  const response = await axiosInstance.post<SecurityApiResponse<ParkingSlot>>(
    `/api/security/parking-slots/${slotId}/release`,
    {}
  );
  return (response.data.data || response.data.message) as ParkingSlot;
};

export interface BoundaryBreach {
  _id?: string;
  id?: string;
  breachNumber?: string;
  breachType?: string;
  location?: string;
  description?: string;
  severity?: string;
  detectedAt?: string;
  status?: string;
  securityIncidentId?: string;
}

export const getBoundaryBreaches = async (
  pagination?: PaginationParams
): Promise<PaginatedResponse<BoundaryBreach>> => {
  const params: Record<string, number> = {};
  if (pagination) {
    params.limit = pagination.limit || 50;
    params.skip = pagination.page ? (pagination.page - 1) * (pagination.limit || 50) : 0;
  }
  const response = await axiosInstance.get<SecurityApiResponse<any>>(
    "/api/security/boundary-breaches",
    { params }
  );
  const backendData = response.data.data || response.data.message;
  if (backendData?.data && Array.isArray(backendData.data)) {
    return {
      data: backendData.data,
      total: backendData.total || backendData.data.length,
      page: pagination?.page || 1,
      limit: backendData.limit || 50,
      totalPages: Math.ceil(
        (backendData.total || backendData.data.length) / (backendData.limit || 50)
      ),
    };
  }
  if (Array.isArray(backendData)) {
    return {
      data: backendData,
      total: backendData.length,
      page: 1,
      limit: backendData.length,
      totalPages: 1,
    };
  }
  return { data: [], total: 0, page: 1, limit: 50, totalPages: 0 };
};

export const createBoundaryBreach = async (
  data: Partial<BoundaryBreach> & { breachType: string; location: string }
): Promise<BoundaryBreach> => {
  const response = await axiosInstance.post<SecurityApiResponse<BoundaryBreach>>(
    "/api/security/boundary-breaches",
    data
  );
  return (response.data.data || response.data.message) as BoundaryBreach;
};

// ============================================================================
// DASHBOARD API
// ============================================================================

export const getSecurityDashboardStats = async (
  filters?: SecuritySearchFilters
): Promise<SecurityDashboardStats> => {
  const params: any = filters || {};
  const response = await axiosInstance.get<SecurityApiResponse<any>>(
    "/api/security/dashboard/stats",
    { params }
  );
  
  const backendData = response.data.data || response.data.message;
  
  // Transform backend response to frontend format
  if (backendData) {
    return {
      totalActiveIncidents: backendData.totalActiveIncidents || backendData.activeIncidents || 0,
      totalAccessZones: backendData.totalAccessZones || 0,
      totalCameras: backendData.totalCameras || 0,
      activeCameras: backendData.activeCameras || ((backendData.totalCameras || 0) - (backendData.offlineCameras || 0)),
      totalVisitorsToday: backendData.totalVisitorsToday || 0,
      activeVisitors: backendData.activeVisitors || 0,
      totalVehicles: backendData.totalVehicles || 0,
      recentIncidents: backendData.recentIncidents || [],
      recentAccessEvents: backendData.recentAccessEvents || []
    };
  }
  
  return {
    totalActiveIncidents: 0,
    totalAccessZones: 0,
    totalCameras: 0,
    activeCameras: 0,
    totalVisitorsToday: 0,
    activeVisitors: 0,
    totalVehicles: 0,
    recentIncidents: [],
    recentAccessEvents: []
  };
};

// ============================================================================
// ASSET MOVEMENT API
// ============================================================================

export interface AssetMovement {
  _id?: string;
  id?: string;
  assetId: string;
  assetName: string;
  assetTag?: string;
  fromZone?: string;
  toZone: string;
  fromZoneName?: string;
  toZoneName?: string;
  fromLocation?: string;
  toLocation: string;
  movementType: "Authorized" | "Unauthorized" | "Scheduled" | "Emergency";
  detectedBy: "RFID Reader" | "Barcode Scanner" | "Manual Entry" | "Camera";
  detectedAt: string;
  authorizedBy?: string;
  reason?: string;
  status: "Pending" | "Approved" | "Rejected" | "Alert";
  alertLevel: "None" | "Low" | "Medium" | "High" | "Critical";
  securityEventType?:
    | "AuthorizedMovement"
    | "UnauthorizedMovement"
    | "AssetLeftPremise"
    | "TamperDetected";
  securityIncidentId?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface AssetMovementFormData {
  assetId: string;
  assetName: string;
  assetTag?: string;
  fromZone?: string;
  toZone: string;
  fromLocation?: string;
  toLocation: string;
  movementType: "Authorized" | "Unauthorized" | "Scheduled" | "Emergency";
  detectedBy: "RFID Reader" | "Barcode Scanner" | "Manual Entry" | "Camera";
  detectedAt: string;
  authorizedBy?: string;
  reason?: string;
}

export const getAllAssetMovements = async (
  filters?: SecuritySearchFilters,
  pagination?: PaginationParams
): Promise<PaginatedResponse<AssetMovement>> => {
  const params: any = {};
  if (filters) {
    if (filters.region) params.region = filters.region;
    if (filters.premise) params.premise = filters.premise;
    if (filters.status) params.status = filters.status;
    if (filters.securityEventType)
      params.securityEventType = filters.securityEventType;
    if (filters.searchQuery) params.where = JSON.stringify({ $or: [
      { assetName: { $regex: filters.searchQuery, $options: 'i' } },
      { assetTag: { $regex: filters.searchQuery, $options: 'i' } }
    ]});
  }
  if (pagination) {
    params.limit = pagination.limit || 20;
    params.skip = pagination.page ? (pagination.page - 1) * (pagination.limit || 20) : 0;
  }
  
  const response = await axiosInstance.get<SecurityApiResponse<any>>(
    "/api/security/asset-movements",
    { params }
  );
  
  const backendData = response.data.data || response.data.message;
  if (backendData && Array.isArray(backendData)) {
    return {
      data: backendData,
      total: backendData.length,
      page: pagination?.page || 1,
      limit: pagination?.limit || 20,
      totalPages: Math.ceil(backendData.length / (pagination?.limit || 20))
    };
  }
  
  if (backendData && backendData.data) {
    return {
      data: backendData.data,
      total: backendData.total || backendData.data.length,
      page: pagination?.page || Math.floor((backendData.skip || 0) / (backendData.limit || 20)) + 1,
      limit: backendData.limit || 20,
      totalPages: Math.ceil((backendData.total || backendData.data.length) / (backendData.limit || 20))
    };
  }
  
  return {
    data: [],
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 0
  };
};

export const getAssetMovementById = async (id: string): Promise<AssetMovement> => {
  const response = await axiosInstance.get<SecurityApiResponse<AssetMovement>>(
    `/api/security/asset-movements/${id}`
  );
  return (response.data.data || response.data.message) as AssetMovement;
};

export const createAssetMovement = async (data: AssetMovementFormData): Promise<AssetMovement> => {
  const response = await axiosInstance.post<SecurityApiResponse<AssetMovement>>(
    "/api/security/asset-movements",
    data
  );
  return (response.data.data || response.data.message) as AssetMovement;
};

export const approveAssetMovement = async (id: string): Promise<AssetMovement> => {
  const response = await axiosInstance.post<SecurityApiResponse<AssetMovement>>(
    `/api/security/asset-movements/${id}/approve`
  );
  return (response.data.data || response.data.message) as AssetMovement;
};

export const scanAssetTag = async (
  assetTag: string,
  zoneId: string,
  location: string
): Promise<any> => {
  const response = await axiosInstance.post<SecurityApiResponse<any>>(
    "/api/security/asset-movements/scan-tag",
    { assetTag, zoneId, location }
  );
  return response.data.data || response.data;
};

export const reportAssetTamper = async (body: {
  assetTag?: string;
  assetId?: string;
  zoneId?: string;
  location?: string;
  notes?: string;
}): Promise<{ movement: AssetMovement }> => {
  const response = await axiosInstance.post<SecurityApiResponse<any>>(
    "/api/security/asset-movements/report-tamper",
    body
  );
  const data = response.data.data || response.data;
  return data as { movement: AssetMovement };
};

// ============================================================================
// THEFT/LOSS REPORT API
// ============================================================================

export interface TheftLossReport {
  _id?: string;
  id?: string;
  reportNumber: string;
  assetId: string;
  assetName: string;
  assetTag?: string;
  reportType: "Theft" | "Loss" | "Missing" | "Damaged";
  reportedBy: string;
  reportedAt: string;
  location: string;
  zone?: string;
  description: string;
  estimatedValue?: number;
  policeReportNumber?: string;
  policeReportDate?: string;
  insuranceClaimNumber?: string;
  insuranceClaimDate?: string;
  status: "Reported" | "Under Investigation" | "Recovered" | "Written Off" | "Claim Filed" | "Closed";
  recoveryDate?: string;
  recoveryLocation?: string;
  recoveryNotes?: string;
  securityIncidentId?: string;
  attachments?: string[];
  createdAt?: string;
  updatedAt?: string;
}

export interface TheftLossReportFormData {
  assetId: string;
  assetName: string;
  assetTag?: string;
  reportType: "Theft" | "Loss" | "Missing" | "Damaged";
  location: string;
  zone?: string;
  description: string;
  estimatedValue?: number;
  policeReportNumber?: string;
  policeReportDate?: string;
  insuranceClaimNumber?: string;
  insuranceClaimDate?: string;
}

export const getAllTheftLossReports = async (
  filters?: SecuritySearchFilters,
  pagination?: PaginationParams
): Promise<PaginatedResponse<TheftLossReport>> => {
  const params: any = {};
  if (filters) {
    if (filters.region) params.region = filters.region;
    if (filters.premise) params.premise = filters.premise;
    if (filters.status) params.status = filters.status;
    if (filters.searchQuery) params.where = JSON.stringify({ $or: [
      { assetName: { $regex: filters.searchQuery, $options: 'i' } },
      { reportNumber: { $regex: filters.searchQuery, $options: 'i' } },
      { assetTag: { $regex: filters.searchQuery, $options: 'i' } }
    ]});
  }
  if (pagination) {
    params.limit = pagination.limit || 20;
    params.skip = pagination.page ? (pagination.page - 1) * (pagination.limit || 20) : 0;
  }
  
  const response = await axiosInstance.get<SecurityApiResponse<any>>(
    "/api/security/theft-loss",
    { params }
  );
  
  const backendData = response.data.data || response.data.message;
  if (backendData && Array.isArray(backendData)) {
    return {
      data: backendData,
      total: backendData.length,
      page: pagination?.page || 1,
      limit: pagination?.limit || 20,
      totalPages: Math.ceil(backendData.length / (pagination?.limit || 20))
    };
  }
  
  if (backendData && backendData.data) {
    return {
      data: backendData.data,
      total: backendData.total || backendData.data.length,
      page: pagination?.page || Math.floor((backendData.skip || 0) / (backendData.limit || 20)) + 1,
      limit: backendData.limit || 20,
      totalPages: Math.ceil((backendData.total || backendData.data.length) / (backendData.limit || 20))
    };
  }
  
  return {
    data: [],
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 0
  };
};

export const getTheftLossReportById = async (id: string): Promise<TheftLossReport> => {
  const response = await axiosInstance.get<SecurityApiResponse<TheftLossReport>>(
    `/api/security/theft-loss/${id}`
  );
  return (response.data.data || response.data.message) as TheftLossReport;
};

export const createTheftLossReport = async (data: TheftLossReportFormData): Promise<TheftLossReport> => {
  const response = await axiosInstance.post<SecurityApiResponse<TheftLossReport>>(
    "/api/security/theft-loss",
    data
  );
  return (response.data.data || response.data.message) as TheftLossReport;
};

export const updateTheftLossReport = async (
  id: string,
  data: Partial<TheftLossReportFormData>
): Promise<TheftLossReport> => {
  const response = await axiosInstance.put<SecurityApiResponse<TheftLossReport>>(
    `/api/security/theft-loss/${id}`,
    data
  );
  return (response.data.data || response.data.message) as TheftLossReport;
};

export const markAssetRecovered = async (
  id: string,
  recoveryLocation: string,
  recoveryNotes: string
): Promise<TheftLossReport> => {
  const response = await axiosInstance.post<SecurityApiResponse<TheftLossReport>>(
    `/api/security/theft-loss/${id}/recover`,
    { recoveryLocation, recoveryNotes }
  );
  return (response.data.data || response.data.message) as TheftLossReport;
};

// ============================================================================
// THREAT REPORT API
// ============================================================================

export interface ThreatReport {
  _id?: string;
  id?: string;
  reportNumber?: string;
  threatType: "Physical Threat" | "Verbal Abuse" | "Harassment" | "Suspicious Person" | "Suspicious Activity" | "Other";
  description: string;
  location: string;
  zone?: string;
  reportedAt: string;
  reportedBy?: string;
  reportedByName?: string;
  isAnonymous: boolean;
  severity: "Low" | "Medium" | "High" | "Critical";
  status: "Reported" | "Under Review" | "Investigating" | "Resolved" | "Closed";
  actionTaken?: string;
  assignedTo?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface ThreatReportFormData {
  threatType: "Physical Threat" | "Verbal Abuse" | "Harassment" | "Suspicious Person" | "Suspicious Activity" | "Other";
  description: string;
  location: string;
  zone?: string;
  isAnonymous: boolean;
  severity: "Low" | "Medium" | "High" | "Critical";
}

export const getAllThreatReports = async (
  filters?: SecuritySearchFilters,
  pagination?: PaginationParams
): Promise<PaginatedResponse<ThreatReport>> => {
  const params: any = {};
  if (filters) {
    if (filters.region) params.region = filters.region;
    if (filters.premise) params.premise = filters.premise;
    if (filters.status) params.status = filters.status;
    if (filters.severity) params.severity = filters.severity;
    if (filters.searchQuery) params.where = JSON.stringify({ $or: [
      { description: { $regex: filters.searchQuery, $options: 'i' } },
      { location: { $regex: filters.searchQuery, $options: 'i' } }
    ]});
  }
  if (pagination) {
    params.limit = pagination.limit || 20;
    params.skip = pagination.page ? (pagination.page - 1) * (pagination.limit || 20) : 0;
  }
  
  const response = await axiosInstance.get<SecurityApiResponse<any>>(
    "/api/security/threat-reports",
    { params }
  );
  
  const backendData = response.data.data || response.data.message;
  if (backendData && Array.isArray(backendData)) {
    return {
      data: backendData,
      total: backendData.length,
      page: pagination?.page || 1,
      limit: pagination?.limit || 20,
      totalPages: Math.ceil(backendData.length / (pagination?.limit || 20))
    };
  }
  
  if (backendData && backendData.data) {
    return {
      data: backendData.data,
      total: backendData.total || backendData.data.length,
      page: pagination?.page || Math.floor((backendData.skip || 0) / (backendData.limit || 20)) + 1,
      limit: backendData.limit || 20,
      totalPages: Math.ceil((backendData.total || backendData.data.length) / (backendData.limit || 20))
    };
  }
  
  return {
    data: [],
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 0
  };
};

export const createThreatReport = async (data: ThreatReportFormData): Promise<ThreatReport> => {
  const response = await axiosInstance.post<SecurityApiResponse<ThreatReport>>(
    "/api/security/threat-reports",
    {
      ...data,
      reportedAt: new Date().toISOString()
    }
  );
  return (response.data.data || response.data.message) as ThreatReport;
};

export const updateThreatReportStatus = async (
  id: string,
  status: string,
  actionTaken?: string
): Promise<ThreatReport> => {
  const response = await axiosInstance.put<SecurityApiResponse<ThreatReport>>(
    `/api/security/threat-reports/${id}`,
    { status, actionTaken }
  );
  return (response.data.data || response.data.message) as ThreatReport;
};

// ============================================================================
// NIGHT SHIFT CHECK-IN API
// ============================================================================

export interface NightShiftCheckIn {
  _id?: string;
  id?: string;
  staffId: string;
  staffName: string;
  department?: string;
  shiftDate: string;
  shiftStart: string;
  shiftEnd: string;
  checkInTime?: string;
  checkOutTime?: string;
  location: string;
  zone?: string;
  status: "Scheduled" | "Checked In" | "Checked Out" | "Missed" | "Late";
  safetyStatus: "Safe" | "Alert" | "Emergency" | "Unknown";
  lastSafetyConfirmation?: string;
  notes?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface NightShiftCheckInFormData {
  staffId: string;
  staffName: string;
  department?: string;
  shiftDate: string;
  shiftStart: string;
  shiftEnd: string;
  location: string;
  zone?: string;
}

export const getAllNightShiftCheckIns = async (
  filters?: SecuritySearchFilters,
  pagination?: PaginationParams
): Promise<PaginatedResponse<NightShiftCheckIn>> => {
  const params: any = {};
  if (filters) {
    if (filters.region) params.region = filters.region;
    if (filters.premise) params.premise = filters.premise;
    if (filters.status) params.status = filters.status;
    if (filters.dateFrom) params.shiftDate = filters.dateFrom;
    if (filters.searchQuery) params.where = JSON.stringify({ $or: [
      { staffName: { $regex: filters.searchQuery, $options: 'i' } },
      { location: { $regex: filters.searchQuery, $options: 'i' } }
    ]});
  }
  if (pagination) {
    params.limit = pagination.limit || 20;
    params.skip = pagination.page ? (pagination.page - 1) * (pagination.limit || 20) : 0;
  }
  
  const response = await axiosInstance.get<SecurityApiResponse<any>>(
    "/api/security/night-shift-checkins",
    { params }
  );
  
  const backendData = response.data.data || response.data.message;
  if (backendData && Array.isArray(backendData)) {
    return {
      data: backendData,
      total: backendData.length,
      page: pagination?.page || 1,
      limit: pagination?.limit || 20,
      totalPages: Math.ceil(backendData.length / (pagination?.limit || 20))
    };
  }
  
  if (backendData && backendData.data) {
    return {
      data: backendData.data,
      total: backendData.total || backendData.data.length,
      page: pagination?.page || Math.floor((backendData.skip || 0) / (backendData.limit || 20)) + 1,
      limit: backendData.limit || 20,
      totalPages: Math.ceil((backendData.total || backendData.data.length) / (backendData.limit || 20))
    };
  }
  
  return {
    data: [],
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 0
  };
};

export const createNightShiftCheckIn = async (data: NightShiftCheckInFormData): Promise<NightShiftCheckIn> => {
  const response = await axiosInstance.post<SecurityApiResponse<NightShiftCheckIn>>(
    "/api/security/night-shift-checkins",
    data
  );
  return (response.data.data || response.data.message) as NightShiftCheckIn;
};

export const performCheckIn = async (id: string): Promise<NightShiftCheckIn> => {
  const response = await axiosInstance.post<SecurityApiResponse<NightShiftCheckIn>>(
    `/api/security/night-shift-checkins/${id}/check-in`
  );
  return (response.data.data || response.data.message) as NightShiftCheckIn;
};

export const performCheckOut = async (id: string): Promise<NightShiftCheckIn> => {
  const response = await axiosInstance.post<SecurityApiResponse<NightShiftCheckIn>>(
    `/api/security/night-shift-checkins/${id}/check-out`
  );
  return (response.data.data || response.data.message) as NightShiftCheckIn;
};

export const confirmSafety = async (id: string, notes?: string): Promise<NightShiftCheckIn> => {
  const response = await axiosInstance.post<SecurityApiResponse<NightShiftCheckIn>>(
    `/api/security/night-shift-checkins/${id}/safety-confirm`,
    { notes, confirmedAt: new Date().toISOString() }
  );
  return (response.data.data || response.data.message) as NightShiftCheckIn;
};

export const triggerSafetyAlert = async (id: string, alertType: string): Promise<NightShiftCheckIn> => {
  const response = await axiosInstance.post<SecurityApiResponse<NightShiftCheckIn>>(
    `/api/security/night-shift-checkins/${id}/safety-alert`,
    { alertType, triggeredAt: new Date().toISOString() }
  );
  return (response.data.data || response.data.message) as NightShiftCheckIn;
};

// ============================================================================
// STAFF SAFETY: MOBILE PANIC, LONE WORKER, GEOFENCE
// ============================================================================

export const triggerMobilePanicButton = async (body: {
  location?: string;
  zone?: string;
  description?: string;
  coordinates?: { lat: number; lng: number };
  panicSource?: "mobile" | "wall_mounted";
}): Promise<Record<string, unknown>> => {
  const response = await axiosInstance.post<SecurityApiResponse<Record<string, unknown>>>(
    "/api/security/mobile/panic-button",
    {
      panicSource: body.panicSource ?? "mobile",
      location: body.location,
      zone: body.zone,
      description: body.description,
      coordinates: body.coordinates,
    }
  );
  return (response.data.data || response.data.message) as Record<string, unknown>;
};

export interface LoneWorkerSession {
  _id?: string;
  id?: string;
  staffId?: string;
  staffName?: string;
  department?: string;
  location?: string;
  zone?: string;
  taskDescription?: string;
  checkInTime?: string;
  lastCheckInTime?: string;
  checkOutTime?: string;
  status?: string;
  alertLevel?: string;
  missedCheckIns?: number;
  createdAt?: string;
  updatedAt?: string;
}

export const getAllLoneWorkers = async (
  pagination?: PaginationParams
): Promise<PaginatedResponse<LoneWorkerSession>> => {
  const params: Record<string, number> = {};
  if (pagination) {
    params.limit = pagination.limit || 50;
    params.skip = pagination.page ? (pagination.page - 1) * (pagination.limit || 50) : 0;
  }
  const response = await axiosInstance.get<SecurityApiResponse<any>>(
    "/api/security/lone-workers",
    { params }
  );
  const backendData = response.data.data || response.data.message;
  if (backendData?.data && Array.isArray(backendData.data)) {
    return {
      data: backendData.data,
      total: backendData.total || backendData.data.length,
      page: pagination?.page || 1,
      limit: backendData.limit || 50,
      totalPages: Math.ceil((backendData.total || backendData.data.length) / (backendData.limit || 50)),
    };
  }
  if (Array.isArray(backendData)) {
    return {
      data: backendData,
      total: backendData.length,
      page: 1,
      limit: backendData.length,
      totalPages: 1,
    };
  }
  return { data: [], total: 0, page: 1, limit: 50, totalPages: 0 };
};

export const createLoneWorkerSession = async (
  data: Partial<LoneWorkerSession> & { staffName: string }
): Promise<LoneWorkerSession> => {
  const response = await axiosInstance.post<SecurityApiResponse<LoneWorkerSession>>(
    "/api/security/lone-workers",
    data
  );
  return (response.data.data || response.data.message) as LoneWorkerSession;
};

export const loneWorkerCheckIn = async (
  id: string,
  body?: { location?: string; zone?: string }
): Promise<LoneWorkerSession> => {
  const response = await axiosInstance.post<SecurityApiResponse<LoneWorkerSession>>(
    `/api/security/lone-workers/${id}/check-in`,
    body || {}
  );
  return (response.data.data || response.data.message) as LoneWorkerSession;
};

export const loneWorkerCheckOut = async (id: string): Promise<LoneWorkerSession> => {
  const response = await axiosInstance.post<SecurityApiResponse<LoneWorkerSession>>(
    `/api/security/lone-workers/${id}/check-out`,
    {}
  );
  return (response.data.data || response.data.message) as LoneWorkerSession;
};

export interface GeoFence {
  _id?: string;
  id?: string;
  fenceName?: string;
  fenceType?: string;
  alertLevel?: string;
  isActive?: boolean;
  center?: { lat: number; lng: number };
  radius?: number;
  boundaries?: { lat: number; lng: number }[];
  premise?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface GeoFenceViolation {
  _id?: string;
  id?: string;
  fenceId?: string;
  fenceName?: string;
  userId?: string;
  violationType?: string;
  location?: { lat: number; lng: number };
  timestamp?: string;
  alertLevel?: string;
  status?: string;
}

export const getAllGeoFences = async (
  pagination?: PaginationParams
): Promise<PaginatedResponse<GeoFence>> => {
  const params: Record<string, number> = {};
  if (pagination) {
    params.limit = pagination.limit || 50;
    params.skip = pagination.page ? (pagination.page - 1) * (pagination.limit || 50) : 0;
  }
  const response = await axiosInstance.get<SecurityApiResponse<any>>("/api/security/geofences", {
    params,
  });
  const backendData = response.data.data || response.data.message;
  if (backendData?.data && Array.isArray(backendData.data)) {
    return {
      data: backendData.data,
      total: backendData.total || backendData.data.length,
      page: pagination?.page || 1,
      limit: backendData.limit || 50,
      totalPages: Math.ceil((backendData.total || backendData.data.length) / (backendData.limit || 50)),
    };
  }
  if (Array.isArray(backendData)) {
    return {
      data: backendData,
      total: backendData.length,
      page: 1,
      limit: backendData.length,
      totalPages: 1,
    };
  }
  return { data: [], total: 0, page: 1, limit: 50, totalPages: 0 };
};

export const createGeoFence = async (data: Partial<GeoFence>): Promise<GeoFence> => {
  const response = await axiosInstance.post<SecurityApiResponse<GeoFence>>(
    "/api/security/geofences",
    data
  );
  return (response.data.data || response.data.message) as GeoFence;
};

export const checkGeoFenceLocation = async (body: {
  userId: string;
  location: { lat: number; lng: number };
}): Promise<{ violations: GeoFenceViolation[]; hasViolations: boolean }> => {
  const response = await axiosInstance.post<{
    success?: boolean;
    violations?: GeoFenceViolation[];
    hasViolations?: boolean;
  }>("/api/security/geofences/check-location", body);
  return {
    violations: response.data.violations || [],
    hasViolations: !!response.data.hasViolations,
  };
};

export const getGeoFenceViolations = async (
  pagination?: PaginationParams,
  status?: string
): Promise<PaginatedResponse<GeoFenceViolation>> => {
  const params: Record<string, string | number> = {};
  if (pagination) {
    params.limit = pagination.limit || 50;
    params.skip = pagination.page ? (pagination.page - 1) * (pagination.limit || 50) : 0;
  }
  if (status) params.status = status;
  const response = await axiosInstance.get<{
    success?: boolean;
    data?: GeoFenceViolation[];
    total?: number;
    limit?: number;
    skip?: number;
  }>("/api/security/geofences/violations", { params });
  const res = response.data;
  if (res.success && Array.isArray(res.data)) {
    const lim = res.limit || 50;
    return {
      data: res.data,
      total: res.total ?? res.data.length,
      page: pagination?.page || Math.floor((res.skip || 0) / lim) + 1,
      limit: lim,
      totalPages: Math.ceil((res.total ?? res.data.length) / lim),
    };
  }
  return { data: [], total: 0, page: 1, limit: 50, totalPages: 0 };
};

// ============================================================================
// CYBER-PHYSICAL & UNIFIED THREAT (§2.8)
// ============================================================================

export interface UnifiedThreatZoneAgg {
  location: string;
  incidents: number;
  violations: number;
  breaches: number;
  geofenceViolations: number;
  restrictedVehicles: number;
  unauthorizedMovements: number;
  cyberAlerts?: number;
  criticalThreats: number;
  highThreats: number;
  totalThreats: number;
  threatScore: number;
}

export interface UnifiedThreatDashboardData {
  threatMap: UnifiedThreatZoneAgg[];
  recentThreats: Record<string, unknown>[];
  summary: {
    totalThreats: number;
    criticalThreats: number;
    highThreats: number;
    locations: number;
    cyberAlertsInPeriod?: number;
  };
  period: string;
}

export const getUnifiedThreatDashboard = async (
  days: number = 7
): Promise<UnifiedThreatDashboardData | null> => {
  const response = await axiosInstance.get<
    SecurityApiResponse<UnifiedThreatDashboardData>
  >("/api/security/unified-threat-dashboard", { params: { days } });
  const payload = response.data.data ?? response.data.message;
  return (payload as UnifiedThreatDashboardData) || null;
};

export interface CyberPhysicalCorrelationRow {
  physicalIncident: Record<string, unknown>;
  relatedAccessViolations: Record<string, unknown>[];
  relatedITAlerts: Record<string, unknown>[];
  correlationScore: number;
  timeWindow: number;
}

export interface CyberPhysicalCorrelationData {
  correlations: CyberPhysicalCorrelationRow[];
  totalCorrelations: number;
  timeWindow: string;
  cyberAlertCount?: number;
}

export const getCyberPhysicalCorrelation = async (
  timeWindowMinutes: number = 60
): Promise<CyberPhysicalCorrelationData | null> => {
  const response = await axiosInstance.get<
    SecurityApiResponse<CyberPhysicalCorrelationData>
  >("/api/security/cyber-physical-correlation", {
    params: { timeWindow: timeWindowMinutes },
  });
  const payload = response.data.data ?? response.data.message;
  return (payload as CyberPhysicalCorrelationData) || null;
};

export type CyberAlertCategory =
  | "it_security"
  | "network_intrusion"
  | "siem_other";

export interface CyberSecurityAlertRecord {
  id?: string;
  _id?: string;
  alertCategory?: CyberAlertCategory;
  title?: string;
  severity?: string;
  alertDateTime?: string;
  zone?: string;
  location?: string;
  source?: string;
  description?: string;
}

export const getCyberSecurityAlerts = async (params?: {
  limit?: number;
  skip?: number;
  alertCategory?: CyberAlertCategory;
}): Promise<{ data: CyberSecurityAlertRecord[]; total: number }> => {
  const response = await axiosInstance.get("/api/security/cyber-alerts", {
    params: {
      limit: params?.limit ?? 40,
      skip: params?.skip ?? 0,
      alertCategory: params?.alertCategory,
    },
  });
  const body = response.data as {
    data?: CyberSecurityAlertRecord[];
    total?: number;
  };
  const list = body.data ?? [];
  return { data: list, total: body.total ?? list.length };
};

export const createCyberSecurityAlert = async (payload: {
  alertCategory?: CyberAlertCategory;
  title: string;
  severity?: string;
  alertDateTime?: string;
  zone?: string;
  location?: string;
  source?: string;
  description?: string;
  externalId?: string;
}): Promise<CyberSecurityAlertRecord | null> => {
  const response = await axiosInstance.post("/api/security/cyber-alerts", payload);
  const body = response.data as { data?: CyberSecurityAlertRecord };
  return body.data ?? null;
};

// ============================================================================
// SECURITY INTEGRATIONS & INTEROPERABILITY
// ============================================================================

export type IntegrationConnectorStatus =
  | "partner_setup"
  | "in_progress"
  | "live"
  | "paused"
  | "api_ready";

export interface IntegrationConnectorConfig {
  status?: IntegrationConnectorStatus;
  vendor?: string;
  notes?: string;
}

export interface SecurityIntegrationSettings {
  tenantId?: string;
  connectors: Record<string, IntegrationConnectorConfig>;
  eventPush: {
    enabled: boolean;
    webhookUrl: string;
    sharedSecret?: string;
    sharedSecretSet?: boolean;
    sharedSecretLast4?: string;
    lastDeliveryAt?: string | null;
    lastDeliveryStatus?: string | null;
    lastError?: string | null;
  };
  incidentSync: {
    partnerSystemName?: string;
    lastWatermarkIso?: string | null;
  };
  updatedAt?: string | null;
}

export interface SecurityIntegrationOverview {
  connectors: Record<string, IntegrationConnectorConfig>;
  messaging: {
    notificationsLast7d: number;
    byChannel: Record<string, number>;
  };
  cameras: {
    total: number;
    active: number;
    maintenance: number;
    other: number;
  };
  visitors: { checkedInNow: number };
  incidents: { createdLast24h: number; createdLast7d: number };
  eventPush: {
    enabled: boolean;
    webhookUrlConfigured: boolean;
    lastDeliveryAt: string | null;
    lastDeliveryStatus: string | null;
    lastError: string | null;
  };
  incidentSync: {
    partnerSystemName: string;
    lastWatermarkIso: string | null;
  };
}

export const getSecurityIntegrationOverview =
  async (): Promise<SecurityIntegrationOverview | null> => {
    const response = await axiosInstance.get("/api/security/integrations/overview");
    const body = response.data as { data?: SecurityIntegrationOverview };
    return body.data ?? null;
  };

export const getSecurityIntegrationSettings =
  async (): Promise<SecurityIntegrationSettings | null> => {
    const response = await axiosInstance.get(
      "/api/security/integrations/settings"
    );
    const body = response.data as { data?: SecurityIntegrationSettings };
    return body.data ?? null;
  };

export const updateSecurityIntegrationSettings = async (
  patch: Partial<{
    connectors: Record<string, Partial<IntegrationConnectorConfig>>;
    eventPush: Partial<{
      enabled: boolean;
      webhookUrl: string;
      sharedSecret: string;
      clearSecret: boolean;
    }>;
    incidentSync: Partial<{
      partnerSystemName: string;
      lastWatermarkIso: string;
    }>;
  }>
): Promise<SecurityIntegrationSettings | null> => {
  const response = await axiosInstance.put(
    "/api/security/integrations/settings",
    patch
  );
  const body = response.data as { data?: SecurityIntegrationSettings };
  return body.data ?? null;
};

export const testSecurityIntegrationEventPush = async (): Promise<
  Record<string, unknown> | null
> => {
  const response = await axiosInstance.post(
    "/api/security/integrations/event-push/test"
  );
  const body = response.data as { data?: Record<string, unknown> };
  return body.data ?? null;
};

export const validateVisitorPassIntegration = async (params: {
  visitorId?: string;
  idNumber?: string;
  mobileNumber?: string;
}): Promise<{
  valid: boolean;
  reasons: string[];
  visitor: Record<string, unknown> | null;
} | null> => {
  const response = await axiosInstance.post(
    "/api/security/integrations/visitors/validate-pass",
    params
  );
  const body = response.data as {
    data?: {
      valid: boolean;
      reasons: string[];
      visitor: Record<string, unknown> | null;
    };
  };
  return body.data ?? null;
};

export const exportSecurityIncidentsForSync = async (params?: {
  since?: string;
  limit?: number;
}): Promise<{ count: number; incidents: Record<string, unknown>[] } | null> => {
  const response = await axiosInstance.get(
    "/api/security/integrations/incidents/export",
    { params: { since: params?.since, limit: params?.limit ?? 100 } }
  );
  const body = response.data as {
    data?: { count: number; incidents: Record<string, unknown>[] };
  };
  return body.data ?? null;
};

export const acknowledgeSecurityIncidentSyncWatermark = async (
  lastWatermarkIso?: string
): Promise<{ lastWatermarkIso: string } | null> => {
  const response = await axiosInstance.post(
    "/api/security/integrations/incidents/sync-watermark",
    { lastWatermarkIso }
  );
  const body = response.data as { data?: { lastWatermarkIso: string } };
  return body.data ?? null;
};

export const getSecurityAccessLogs = async (params?: {
  limit?: number;
  skip?: number;
  accessType?: string;
}): Promise<{ data: Record<string, unknown>[]; total: number }> => {
  const q: Record<string, string | number> = {
    limit: params?.limit ?? 50,
    skip: params?.skip ?? 0,
  };
  if (params?.accessType) {
    q.where = JSON.stringify({ accessType: params.accessType });
  }
  const response = await axiosInstance.get<
    SecurityApiResponse<Record<string, unknown>[]> & {
      total?: number;
      data?: Record<string, unknown>[];
    }
  >("/api/security/access-logs", { params: q });
  const body = response.data as any;
  const rows = body.data ?? body.message ?? [];
  const list = Array.isArray(rows) ? rows : rows.data ?? [];
  return { data: list, total: body.total ?? list.length };
};

// ============================================================================
// RISK SCORES (§2.10)
// ============================================================================

export interface RiskScoreRecord {
  _id?: string;
  zoneId?: string;
  zoneName?: string;
  riskScore?: number;
  riskLevel?: string;
  lastCalculatedAt?: string;
  factors?: unknown[];
}

export const getRiskScores = async (params?: {
  limit?: number;
  skip?: number;
}): Promise<{ data: RiskScoreRecord[]; total: number }> => {
  const response = await axiosInstance.get("/api/security/risk-scores", {
    params: { limit: params?.limit ?? 50, skip: params?.skip ?? 0 },
  });
  const body = response.data as any;
  const rows = body.data ?? body.message ?? [];
  const list = Array.isArray(rows) ? rows : [];
  return { data: list, total: body.total ?? list.length };
};

export const calculateRiskScoreForZone = async (
  zoneId: string
): Promise<Record<string, unknown> | null> => {
  const response = await axiosInstance.post(
    `/api/security/risk-scores/${zoneId}/calculate`
  );
  const body = response.data as any;
  return (body.data ?? body.message) as Record<string, unknown> | null;
};

// ============================================================================
// AI / PREDICTIVE SECURITY (rules-based + jurisdiction)
// ============================================================================

export type PredictiveJurisdictionRegion =
  | "EU_GDPR"
  | "UK_GDPR"
  | "US_STATE_LAW"
  | "OTHER";

export interface PredictiveSecuritySettings {
  tenantId?: string;
  jurisdictionRegion: PredictiveJurisdictionRegion;
  biometricAnalyticsEnabled: boolean;
  behaviorAnalyticsEnabled: boolean;
  crowdAnalyticsEnabled: boolean;
  incidentPredictionEnabled: boolean;
  analyticsRetentionDays: number;
  lawfulBasisSummary?: string;
  updatedAt?: string;
}

export interface PredictiveFeatureBlock {
  enabled?: boolean;
  score?: number;
  level?: string;
  signals?: string[];
  methodology?: string;
}

export interface PredictiveIncidentProbability {
  enabled?: boolean;
  tenantScore?: number;
  tenantLevel?: string;
  byZone?: Array<{
    zoneId: string;
    zoneName: string;
    probability: number;
    level: string;
    incidentsLast7d?: number;
  }>;
  horizonHours?: number;
  methodology?: string;
}

export interface PredictiveFacialRecognition {
  biometricOptIn?: boolean;
  jurisdictionRegion?: string;
  operationalStatus?: string;
  detail?: string;
  methodology?: string;
}

export interface PredictiveSecurityOverview {
  evaluationPeriodDays: number;
  settingsSnapshot?: Partial<PredictiveSecuritySettings>;
  suspiciousBehavior: PredictiveFeatureBlock;
  crowdAnomaly: PredictiveFeatureBlock;
  incidentProbability: PredictiveIncidentProbability;
  facialRecognition: PredictiveFacialRecognition;
  disclaimers?: string[];
}

export const getPredictiveSecurityOverview = async (params?: {
  days?: number;
}): Promise<PredictiveSecurityOverview | null> => {
  const response = await axiosInstance.get("/api/security/ai-predictive/overview", {
    params: { days: params?.days ?? 7 },
  });
  const body = response.data as { data?: PredictiveSecurityOverview };
  return body.data ?? null;
};

export const getPredictiveSecuritySettings =
  async (): Promise<PredictiveSecuritySettings | null> => {
    const response = await axiosInstance.get(
      "/api/security/ai-predictive/settings"
    );
    const body = response.data as { data?: PredictiveSecuritySettings };
    return body.data ?? null;
  };

export const updatePredictiveSecuritySettings = async (
  patch: Partial<PredictiveSecuritySettings>
): Promise<PredictiveSecuritySettings | null> => {
  const response = await axiosInstance.put(
    "/api/security/ai-predictive/settings",
    patch
  );
  const body = response.data as { data?: PredictiveSecuritySettings };
  return body.data ?? null;
};

// ============================================================================
// SECURITY NOTIFICATION LOGS (§2.11)
// ============================================================================

export const getSecurityNotificationLogs = async (params?: {
  limit?: number;
  skip?: number;
}): Promise<{ data: Record<string, unknown>[]; total: number }> => {
  const response = await axiosInstance.get("/api/security/notifications/logs", {
    params: { limit: params?.limit ?? 50, skip: params?.skip ?? 0 },
  });
  const body = response.data as any;
  const rows = body.data ?? body.message ?? [];
  const list = Array.isArray(rows) ? rows : [];
  return { data: list, total: body.total ?? list.length };
};

// ============================================================================
// SECURITY COMMAND CENTER (web dashboard — CCTV, incidents, guards, heatmap, broadcast)
// ============================================================================

export const getCommandCenterCctvGrid =
  async (): Promise<CommandCenterCctvGridData | null> => {
    const response = await axiosInstance.get(
      "/api/security/command-center/cctv-grid"
    );
    const body = response.data as { data?: CommandCenterCctvGridData };
    return body.data ?? null;
  };

export const getCommandCenterActiveIncidents = async (): Promise<{
  incidents: SecurityIncident[];
  bySeverity: Record<string, SecurityIncident[]>;
  total: number;
} | null> => {
  const response = await axiosInstance.get(
    "/api/security/command-center/active-incidents"
  );
  const body = response.data as {
    data?: {
      incidents: SecurityIncident[];
      bySeverity: Record<string, SecurityIncident[]>;
      total: number;
    };
  };
  return body.data ?? null;
};

export const getCommandCenterGuardDeployment =
  async (): Promise<CommandCenterGuardDeploymentData | null> => {
    const response = await axiosInstance.get(
      "/api/security/command-center/guard-deployment"
    );
    const body = response.data as { data?: CommandCenterGuardDeploymentData };
    return body.data ?? null;
  };

export const getCommandCenterThreatHeatmap = async (params?: {
  days?: number;
}): Promise<CommandCenterThreatHeatmapData | null> => {
  const response = await axiosInstance.get(
    "/api/security/command-center/threat-heatmap",
    { params: { days: params?.days ?? 30 } }
  );
  const body = response.data as { data?: CommandCenterThreatHeatmapData };
  return body.data ?? null;
};

export const getCommandCenterDashboardSummary =
  async (): Promise<CommandCenterDashboardSummaryData | null> => {
    const response = await axiosInstance.get(
      "/api/security/command-center/dashboard-summary"
    );
    const body = response.data as { data?: CommandCenterDashboardSummaryData };
    return body.data ?? null;
  };

export const sendCommandCenterEmergencyBroadcast = async (
  payload: EmergencyBroadcastPayload
): Promise<EmergencyBroadcastResult | null> => {
  const response = await axiosInstance.post(
    "/api/security/command-center/emergency-broadcast",
    payload
  );
  const body = response.data as {
    success?: boolean;
    data?: EmergencyBroadcastResult;
    message?: string;
  };
  if (body.data) return body.data;
  return {
    id: "",
    message: typeof body.message === "string" ? body.message : "",
  } as EmergencyBroadcastResult;
};
